/**
 * Booking Calendar Component — fleet calendar view with aircraft filter.
 *
 * Renders a 30-day grid showing bookings colour-coded by aircraft,
 * with a dropdown filter to show all aircraft or a single aircraft.
 *
 * Requirements: 12.5
 */

import { FLIGHT_STATUS } from '../utils/constants.js';
import { formatDateUK } from '../utils/format.js';

// ── View modes ────────────────────────────────────────
export const CALENDAR_VIEW = { MONTH: 'month', WEEK: 'week', DAY: 'day' };

// ── Colour palette for aircraft ───────────────────────

const CALENDAR_COLOURS = [
  '#3182ce', '#e53e3e', '#38a169', '#d69e2e', '#805ad5',
  '#dd6b20', '#319795', '#d53f8c', '#2b6cb0', '#c05621',
];

// ── Exported pure helpers for testability ─────────────

/**
 * Filter bookings by aircraft. Returns all bookings if no filter is set.
 * @param {Object[]} bookings
 * @param {string|null} aircraftId - null means show all
 * @returns {Object[]}
 */
export function filterBookingsByAircraft(bookings, aircraftId) {
  if (!Array.isArray(bookings)) return [];
  if (!aircraftId) return bookings;
  return bookings.filter((b) => b.aircraft_id === aircraftId);
}

/**
 * Assign a distinct colour to each aircraft. Returns a Map of aircraftId → colour.
 * @param {Object[]} aircraft
 * @returns {Map<string, string>}
 */
export function assignAircraftColours(aircraft) {
  const map = new Map();
  if (!Array.isArray(aircraft)) return map;
  aircraft.forEach((ac, i) => {
    map.set(ac.id, CALENDAR_COLOURS[i % CALENDAR_COLOURS.length]);
  });
  return map;
}

/**
 * Generate an array of day objects for the calendar.
 * Now delegates to createMonthDays() to produce a proper month-view grid
 * with leading/trailing days and correct weekday alignment.
 * @param {Date} startDate
 * @param {number} numDays
 * @returns {{ date: string, dateObj: Date, isPast: boolean, isToday: boolean, isCurrentMonth: boolean }[]}
 */
export function createCalendarDays(startDate, numDays) {
  const year = startDate.getFullYear();
  const month = startDate.getMonth();
  return createMonthDays(year, month);
}

/**
 * Generate an array of day objects for a full month-view calendar grid.
 * The grid always starts on Monday and ends on Sunday, filling leading days
 * from the previous month and trailing days from the next month.
 *
 * @param {number} year  - Full year (e.g. 2025)
 * @param {number} month - 0-based month (0 = January … 11 = December)
 * @returns {{ date: string, dateObj: Date, isPast: boolean, isToday: boolean, isCurrentMonth: boolean }[]}
 */
export function createMonthDays(year, month) {
  const days = [];
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Weekday offset: Monday=0 … Sunday=6
  const rawDay = firstOfMonth.getDay(); // 0=Sun,1=Mon…6=Sat
  const weekdayOffset = rawDay === 0 ? 6 : rawDay - 1;

  // Leading days from previous month
  for (let i = weekdayOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, 1 - (i + 1));
    // Normalise to avoid DST edge-case drift
    d.setHours(12, 0, 0, 0);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.push({ date: dateStr, dateObj: d, isPast: dateStr < todayStr, isToday: dateStr === todayStr, isCurrentMonth: false });
  }

  // Days of the target month
  const daysInMonth = lastOfMonth.getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    d.setHours(12, 0, 0, 0);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.push({ date: dateStr, dateObj: d, isPast: dateStr < todayStr, isToday: dateStr === todayStr, isCurrentMonth: true });
  }

  // Trailing days from next month to complete the last week row
  const remainder = days.length % 7;
  if (remainder !== 0) {
    const trailingCount = 7 - remainder;
    for (let i = 1; i <= trailingCount; i++) {
      const d = new Date(year, month + 1, i);
      d.setHours(12, 0, 0, 0);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      days.push({ date: dateStr, dateObj: d, isPast: dateStr < todayStr, isToday: dateStr === todayStr, isCurrentMonth: false });
    }
  }

  return days;
}


/**
 * Generate an array of 7 day objects for a week starting on a given Monday.
 * @param {Date} weekStartDate - The Monday to start from
 * @returns {{ date: string, dateObj: Date, isPast: boolean, isToday: boolean, isCurrentMonth: boolean }[]}
 */
export function createWeekDays(weekStartDate) {
  const days = [];
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const refMonth = weekStartDate.getMonth();

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStartDate);
    d.setDate(d.getDate() + i);
    d.setHours(12, 0, 0, 0);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.push({
      date: dateStr,
      dateObj: d,
      isPast: dateStr < todayStr,
      isToday: dateStr === todayStr,
      isCurrentMonth: d.getMonth() === refMonth,
    });
  }
  return days;
}

/**
 * Generate hour slot objects for a day view.
 * @param {number} [startHour=6] - First hour to display
 * @param {number} [endHour=20] - Last hour to display (exclusive)
 * @returns {{ hour: number, label: string }[]}
 */
export function createDayHours(startHour = 6, endHour = 20) {
  const hours = [];
  for (let h = startHour; h < endHour; h++) {
    hours.push({
      hour: h,
      label: `${String(h).padStart(2, '0')}:00`,
    });
  }
  return hours;
}

/**
 * Convert a time string "HH:MM" to a percentage position within a time range.
 * @param {string} time - "HH:MM" or "HH:MM:SS"
 * @param {number} startHour - First visible hour
 * @param {number} endHour - Last visible hour (exclusive)
 * @returns {number} Percentage (0–100), clamped
 */
export function timeToPercent(time, startHour = 6, endHour = 20) {
  if (!time) return 0;
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  const totalMinutes = h * 60 + m;
  const rangeStart = startHour * 60;
  const rangeEnd = endHour * 60;
  const pct = ((totalMinutes - rangeStart) / (rangeEnd - rangeStart)) * 100;
  return Math.max(0, Math.min(100, pct));
}

/**
 * Filter bookings that fall on a specific date.
 * Handles multi-day bookings.
 * @param {Object[]} bookings
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {Object[]}
 */
export function filterBookingsForDate(bookings, dateStr) {
  if (!Array.isArray(bookings)) return [];
  return bookings.filter((b) => {
    if (!b.flight_date) return false;
    const start = b.flight_date;
    const end = b.end_date || start;
    return dateStr >= start && dateStr <= end;
  });
}

/**
 * Build a lookup of dateStr → bookings[] handling multi-day spans.
 * @param {Object[]} bookings
 * @returns {Object<string, Object[]>}
 */
export function buildBookingsByDate(bookings) {
  const map = {};
  if (!Array.isArray(bookings)) return map;
  bookings.forEach((b) => {
    const startDate = b.flight_date;
    if (!startDate) return;
    const endDate = b.end_date || startDate;
    const cur = new Date(startDate + 'T12:00:00');
    const last = new Date(endDate + 'T12:00:00');
    while (cur <= last) {
      const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(b);
      cur.setDate(cur.getDate() + 1);
    }
  });
  return map;
}

/**
 * Get the Monday of the week containing a given date.
 * @param {Date} date
 * @returns {Date}
 */
export function getMonday(date) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/**
 * Create a DOM element for a booking block on the calendar.
 * @param {Object} booking
 * @param {Map<string, string>} colourMap
 * @param {string} [currentDate] - The date this block is being rendered on (YYYY-MM-DD)
 * @returns {HTMLElement}
 */
export function createBookingBlock(booking, colourMap, currentDate) {
  const block = document.createElement('div');
  block.className = 'calendar-booking-block';
  const colour = colourMap.get(booking.aircraft_id) || '#999';
  block.style.backgroundColor = colour;
  block.style.color = '#fff';

  const reg = booking.aircraft?.registration || '—';
  const start = (booking.start_time || '').slice(0, 5);
  const end = (booking.end_time || '').slice(0, 5);
  const pilot = booking.pilots?.name || '—';
  const status = booking.status || '';

  // Determine multi-day position
  const bookingStart = booking.flight_date;
  const bookingEnd = booking.end_date || bookingStart;
  const isMultiDay = bookingStart !== bookingEnd;
  const isFirstDay = currentDate === bookingStart;
  const isLastDay = currentDate === bookingEnd;
  const isMiddleDay = isMultiDay && !isFirstDay && !isLastDay;

  // Status-based styling
  if (status === FLIGHT_STATUS.REJECTED) {
    block.classList.add('calendar-booking-block--rejected');
  } else if (status === FLIGHT_STATUS.FLOWN) {
    block.classList.add('calendar-booking-block--flown');
  }

  block.setAttribute('data-booking-id', booking.id || '');
  block.setAttribute('data-pilot', pilot);
  block.setAttribute('data-status', status);

  // Tooltip with booking details
  const tooltipLines = [
    `Aircraft: ${reg}`,
    `Pilot: ${pilot}`,
    `Time: ${start}–${end}`,
    `Status: ${status}`,
  ];
  if (booking.purpose) tooltipLines.push(`Purpose: ${booking.purpose}`);
  if (isMultiDay) {
    tooltipLines.splice(2, 0, `Dates: ${bookingStart} to ${bookingEnd}`);
  }
  block.title = tooltipLines.join('\n');

  if (isMultiDay) {
    if (isFirstDay) {
      // "G-ALBK 08:00 →"
      const regSpan = document.createElement('span');
      regSpan.className = 'calendar-booking-block__reg';
      regSpan.textContent = reg;
      block.appendChild(regSpan);
      block.appendChild(document.createTextNode(' '));
      const timeSpan = document.createElement('span');
      timeSpan.className = 'calendar-booking-block__time';
      timeSpan.textContent = `${start} →`;
      block.appendChild(timeSpan);
    } else if (isLastDay) {
      // "← G-ALBK 17:00"
      const arrow = document.createElement('span');
      arrow.className = 'calendar-booking-block__time';
      arrow.textContent = '← ';
      block.appendChild(arrow);
      const regSpan = document.createElement('span');
      regSpan.className = 'calendar-booking-block__reg';
      regSpan.textContent = reg;
      block.appendChild(regSpan);
      block.appendChild(document.createTextNode(' '));
      const timeSpan = document.createElement('span');
      timeSpan.className = 'calendar-booking-block__time';
      timeSpan.textContent = end;
      block.appendChild(timeSpan);
    } else {
      // "← G-ALBK →"
      const left = document.createElement('span');
      left.className = 'calendar-booking-block__time';
      left.textContent = '← ';
      block.appendChild(left);
      const regSpan = document.createElement('span');
      regSpan.className = 'calendar-booking-block__reg';
      regSpan.textContent = reg;
      block.appendChild(regSpan);
      block.appendChild(document.createTextNode(' '));
      const right = document.createElement('span');
      right.className = 'calendar-booking-block__time';
      right.textContent = '→';
      block.appendChild(right);
    }
  } else {
    const regSpan = document.createElement('span');
    regSpan.className = 'calendar-booking-block__reg';
    regSpan.textContent = reg;
    block.appendChild(regSpan);
    block.appendChild(document.createTextNode(' '));
    const timeSpan = document.createElement('span');
    timeSpan.className = 'calendar-booking-block__time';
    timeSpan.textContent = `${start}–${end}`;
    block.appendChild(timeSpan);
  }

  // "Needs log" icon for APPROVED past flights without a log
  const todayStr = new Date().toISOString().split('T')[0];
  if (status === FLIGHT_STATUS.APPROVED && booking.flight_date <= todayStr && booking.flight_time_mins == null) {
    const icon = document.createElement('span');
    icon.className = 'calendar-booking-block__needs-log';
    icon.textContent = '\u{1F4DD}';
    icon.title = 'Needs flight log';
    block.appendChild(icon);
    block.setAttribute('data-needs-log', 'true');
  }

  return block;
}

/**
 * Render the fleet calendar with aircraft filter dropdown.
 * Supports month, week, and day views.
 *
 * @param {Object} config
 * @param {Object[]} config.aircraft
 * @param {Object[]} config.bookings
 * @param {number} [config.year]
 * @param {number} [config.month]
 * @param {string} [config.viewMode='month'] - 'month' | 'week' | 'day'
 * @param {Date} [config.weekStart] - Monday of the displayed week (week view)
 * @param {string} [config.dayDate] - YYYY-MM-DD (day view)
 * @param {string|null} [config.selectedAircraftId]
 * @param {Function} [config.onFilterChange]
 * @param {Function} [config.onDayClick]
 * @param {Function} [config.onBookingClick]
 * @param {Function} [config.onPrev]
 * @param {Function} [config.onNext]
 * @param {Function} [config.onViewChange] - (viewMode) => void
 * @returns {HTMLElement}
 */
export function renderBookingCalendar(config) {
  const {
    aircraft = [],
    bookings = [],
    startDate,
    selectedAircraftId = null,
    onFilterChange,
    onDayClick,
    onBookingClick,
    onPrev,
    onNext,
    onViewChange,
    showMyAircraft = false,
    myAircraftChecked = false,
    onMyAircraftChange,
    showMyFlights = false,
    myFlightsChecked = false,
    onMyFlightsChange,
  } = config;

  const viewMode = config.viewMode || CALENDAR_VIEW.MONTH;

  // Derive year/month from config or fall back to startDate
  const calYear = config.year != null ? config.year : (startDate ? startDate.getFullYear() : new Date().getFullYear());
  const calMonth = config.month != null ? config.month : (startDate ? startDate.getMonth() : new Date().getMonth());
  const weekStart = config.weekStart || getMonday(new Date());
  const dayDate = config.dayDate || new Date().toISOString().split('T')[0];

  const container = document.createElement('div');
  container.className = 'multi-calendar';

  const colourMap = assignAircraftColours(aircraft);
  const filteredBookings = filterBookingsByAircraft(bookings, selectedAircraftId);
  const bookingsByDate = buildBookingsByDate(filteredBookings);

  // ── Header with filter and navigation ──
  const header = document.createElement('div');
  header.className = 'multi-calendar__header';

  const prevBtn = document.createElement('button');
  prevBtn.className = 'btn btn--primary multi-calendar__nav-btn';
  prevBtn.textContent = '←';
  prevBtn.title = 'Previous';
  prevBtn.addEventListener('click', () => onPrev && onPrev());

  // Aircraft filter dropdown
  const filterWrapper = document.createElement('div');
  filterWrapper.className = 'multi-calendar__filter';

  const filterLabel = document.createElement('label');
  filterLabel.textContent = 'Aircraft: ';
  filterLabel.setAttribute('for', 'calendar-aircraft-filter');

  const filterSelect = document.createElement('select');
  filterSelect.id = 'calendar-aircraft-filter';
  filterSelect.className = 'multi-calendar__filter-select';
  filterSelect.style.minWidth = '12rem';

  const allOption = document.createElement('option');
  allOption.value = '';
  allOption.textContent = 'All Aircraft';
  filterSelect.appendChild(allOption);

  aircraft.forEach((ac) => {
    const opt = document.createElement('option');
    opt.value = ac.id;
    opt.textContent = `${ac.registration} (${ac.type})`;
    if (ac.id === selectedAircraftId) opt.selected = true;
    filterSelect.appendChild(opt);
  });

  filterSelect.addEventListener('change', () => {
    if (onFilterChange) onFilterChange(filterSelect.value || null);
  });

  filterWrapper.appendChild(filterLabel);
  filterWrapper.appendChild(filterSelect);

  // "My Aircraft" checkbox (for owners)
  if (showMyAircraft) {
    const myAcLabel = document.createElement('label');
    myAcLabel.className = 'aircraft-list__filter-toggle';
    myAcLabel.style.marginLeft = '0.75rem';
    const myAcCb = document.createElement('input');
    myAcCb.type = 'checkbox';
    myAcCb.checked = myAircraftChecked;
    myAcCb.addEventListener('change', () => {
      if (onMyAircraftChange) onMyAircraftChange(myAcCb.checked);
    });
    myAcLabel.appendChild(myAcCb);
    myAcLabel.appendChild(document.createTextNode(' My Aircraft'));
    filterWrapper.appendChild(myAcLabel);
  }

  // "My Flights" checkbox (for owners/pilots)
  if (showMyFlights) {
    const myFlLabel = document.createElement('label');
    myFlLabel.className = 'aircraft-list__filter-toggle';
    myFlLabel.style.marginLeft = '0.75rem';
    const myFlCb = document.createElement('input');
    myFlCb.type = 'checkbox';
    myFlCb.checked = myFlightsChecked;
    myFlCb.addEventListener('change', () => {
      if (onMyFlightsChange) onMyFlightsChange(myFlCb.checked);
    });
    myFlLabel.appendChild(myFlCb);
    myFlLabel.appendChild(document.createTextNode(' My Flights'));
    filterWrapper.appendChild(myFlLabel);
  }

  const nextBtn = document.createElement('button');
  nextBtn.className = 'btn btn--primary multi-calendar__nav-btn';
  nextBtn.textContent = '→';
  nextBtn.title = 'Next';
  nextBtn.addEventListener('click', () => onNext && onNext());

  // View toggle buttons
  const viewToggle = document.createElement('div');
  viewToggle.className = 'multi-calendar__view-toggle';
  [CALENDAR_VIEW.MONTH, CALENDAR_VIEW.WEEK, CALENDAR_VIEW.DAY].forEach((mode) => {
    const btn = document.createElement('button');
    btn.className = `multi-calendar__view-btn${viewMode === mode ? ' multi-calendar__view-btn--active' : ''}`;
    btn.textContent = mode.charAt(0).toUpperCase() + mode.slice(1);
    btn.addEventListener('click', () => {
      if (onViewChange) onViewChange(mode);
    });
    viewToggle.appendChild(btn);
  });

  header.appendChild(prevBtn);
  header.appendChild(filterWrapper);
  header.appendChild(viewToggle);
  header.appendChild(nextBtn);
  container.appendChild(header);

  // ── Colour legend ──
  const legend = document.createElement('div');
  legend.className = 'multi-calendar__legend';
  aircraft.forEach((ac) => {
    if (selectedAircraftId && ac.id !== selectedAircraftId) return;
    const item = document.createElement('span');
    item.className = 'multi-calendar__legend-item';
    const swatch = document.createElement('span');
    swatch.className = 'multi-calendar__legend-swatch';
    swatch.style.backgroundColor = colourMap.get(ac.id);
    item.appendChild(swatch);
    item.appendChild(document.createTextNode(ac.registration));
    legend.appendChild(item);
  });
  // ── Render body based on view mode ──
  if (viewMode === CALENDAR_VIEW.MONTH) {
    renderMonthGrid(container, createMonthDays(calYear, calMonth), bookingsByDate, colourMap, onDayClick, onBookingClick);
  } else if (viewMode === CALENDAR_VIEW.WEEK) {
    renderWeekGrid(container, createWeekDays(weekStart), bookingsByDate, colourMap, onDayClick, onBookingClick);
  } else {
    renderDayGrid(container, dayDate, filteredBookings, aircraft, colourMap, onBookingClick);
  }

  // ── Colour legend (below the calendar grid) ──
  container.appendChild(legend);

  return container;
}

// ── Internal: format Date → YYYY-MM-DD ──
function fmtISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Month grid (original layout) ──
function renderMonthGrid(container, days, bookingsByDate, colourMap, onDayClick, onBookingClick) {
  const weekdayHeader = document.createElement('div');
  weekdayHeader.className = 'multi-calendar__weekday-header';
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach((name) => {
    const span = document.createElement('span');
    span.textContent = name;
    weekdayHeader.appendChild(span);
  });
  container.appendChild(weekdayHeader);

  const grid = document.createElement('div');
  grid.className = 'multi-calendar__grid';

  days.forEach((day) => {
    const cell = document.createElement('div');
    cell.className = 'multi-calendar__day';
    cell.setAttribute('data-date', day.date);

    if (day.isPast) cell.classList.add('past');
    if (day.isToday) cell.classList.add('today');
    if (!day.isCurrentMonth) cell.classList.add('multi-calendar__day--adjacent');

    const label = document.createElement('div');
    label.className = 'multi-calendar__day-label';
    label.textContent = `${day.dateObj.getDate()}`;
    cell.appendChild(label);

    const dayBookings = bookingsByDate[day.date] || [];
    dayBookings.forEach((b) => {
      if (b.status === FLIGHT_STATUS.CANCELLED) return;
      const block = createBookingBlock(b, colourMap, day.date);
      block.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onBookingClick) onBookingClick(b);
      });
      cell.appendChild(block);
    });

    cell.addEventListener('click', () => {
      if (onDayClick) onDayClick(day.date);
    });

    grid.appendChild(cell);
  });

  container.appendChild(grid);
}

// ── Week grid — 7 taller day columns ──
function renderWeekGrid(container, days, bookingsByDate, colourMap, onDayClick, onBookingClick) {
  const weekdayHeader = document.createElement('div');
  weekdayHeader.className = 'multi-calendar__weekday-header';
  days.forEach((day) => {
    const span = document.createElement('span');
    const dayName = day.dateObj.toLocaleDateString('en-GB', { weekday: 'short' });
    span.textContent = `${dayName} ${day.dateObj.getDate()}`;
    if (day.isToday) span.classList.add('multi-calendar__weekday--today');
    weekdayHeader.appendChild(span);
  });
  container.appendChild(weekdayHeader);

  const grid = document.createElement('div');
  grid.className = 'multi-calendar__grid multi-calendar__grid--week';

  days.forEach((day) => {
    const cell = document.createElement('div');
    cell.className = 'multi-calendar__day multi-calendar__day--week';
    cell.setAttribute('data-date', day.date);

    if (day.isPast) cell.classList.add('past');
    if (day.isToday) cell.classList.add('today');

    const dayBookings = bookingsByDate[day.date] || [];
    dayBookings.forEach((b) => {
      if (b.status === FLIGHT_STATUS.CANCELLED) return;
      const block = createBookingBlock(b, colourMap, day.date);
      // Add time detail for week view
      const timeDetail = document.createElement('div');
      timeDetail.className = 'calendar-booking-block__pilot';
      timeDetail.textContent = b.pilots?.name || '';
      block.appendChild(timeDetail);

      block.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onBookingClick) onBookingClick(b);
      });
      cell.appendChild(block);
    });

    cell.addEventListener('click', () => {
      if (onDayClick) onDayClick(day.date);
    });

    grid.appendChild(cell);
  });

  container.appendChild(grid);
}

// ── Day grid — hourly time slots × aircraft columns ──
function renderDayGrid(container, dateStr, allBookings, aircraft, colourMap, onBookingClick) {
  const DAY_START = 6;
  const DAY_END = 21;
  const hours = createDayHours(DAY_START, DAY_END);

  // Filter to bookings on this date
  const dayBookings = filterBookingsForDate(allBookings, dateStr).filter(
    (b) => b.status !== FLIGHT_STATUS.CANCELLED,
  );

  // Determine which aircraft have bookings, or show all visible aircraft
  const visibleAircraft = aircraft.length > 0 ? aircraft : [];

  const wrapper = document.createElement('div');
  wrapper.className = 'multi-calendar__day-view';

  // Column headers: time gutter + one column per aircraft
  const colHeader = document.createElement('div');
  colHeader.className = 'multi-calendar__day-header';
  colHeader.style.gridTemplateColumns = `4rem repeat(${visibleAircraft.length}, 1fr)`;

  const gutterHead = document.createElement('div');
  gutterHead.className = 'multi-calendar__day-gutter-head';
  gutterHead.textContent = 'Time';
  colHeader.appendChild(gutterHead);

  visibleAircraft.forEach((ac) => {
    const acHead = document.createElement('div');
    acHead.className = 'multi-calendar__day-ac-head';
    const swatch = document.createElement('span');
    swatch.className = 'multi-calendar__legend-swatch';
    swatch.style.backgroundColor = colourMap.get(ac.id);
    acHead.appendChild(swatch);
    acHead.appendChild(document.createTextNode(` ${ac.registration}`));
    colHeader.appendChild(acHead);
  });
  wrapper.appendChild(colHeader);

  // Time grid body
  const body = document.createElement('div');
  body.className = 'multi-calendar__day-body';
  body.style.gridTemplateColumns = `4rem repeat(${visibleAircraft.length}, 1fr)`;
  body.style.gridTemplateRows = `repeat(${hours.length}, 1fr)`;

  // Render hour labels in gutter
  hours.forEach((h, rowIdx) => {
    const gutter = document.createElement('div');
    gutter.className = 'multi-calendar__day-gutter';
    gutter.textContent = h.label;
    gutter.style.gridRow = `${rowIdx + 1}`;
    gutter.style.gridColumn = '1';
    body.appendChild(gutter);
  });

  // Render aircraft columns with positioned booking blocks
  visibleAircraft.forEach((ac, colIdx) => {
    // Column background cells
    hours.forEach((h, rowIdx) => {
      const cell = document.createElement('div');
      cell.className = 'multi-calendar__day-cell';
      cell.style.gridRow = `${rowIdx + 1}`;
      cell.style.gridColumn = `${colIdx + 2}`;
      body.appendChild(cell);
    });

    // Overlay container for this aircraft column
    const colOverlay = document.createElement('div');
    colOverlay.className = 'multi-calendar__day-col-overlay';
    colOverlay.style.gridColumn = `${colIdx + 2}`;
    colOverlay.style.gridRow = `1 / ${hours.length + 1}`;

    // Place booking blocks
    const acBookings = dayBookings.filter((b) => b.aircraft_id === ac.id);
    acBookings.forEach((b) => {
      const startPct = timeToPercent(b.start_time, DAY_START, DAY_END);
      const endPct = timeToPercent(b.end_time, DAY_START, DAY_END);
      const height = Math.max(endPct - startPct, 2); // minimum 2% height

      const block = document.createElement('div');
      block.className = 'calendar-booking-block calendar-booking-block--day';
      const colour = colourMap.get(b.aircraft_id) || '#999';
      block.style.backgroundColor = colour;
      block.style.color = '#fff';
      block.style.position = 'absolute';
      block.style.top = `${startPct}%`;
      block.style.height = `${height}%`;
      block.style.left = '2px';
      block.style.right = '2px';
      block.style.borderRadius = '4px';
      block.style.overflow = 'hidden';

      if (b.status === FLIGHT_STATUS.REJECTED) block.classList.add('calendar-booking-block--rejected');
      if (b.status === FLIGHT_STATUS.FLOWN) block.classList.add('calendar-booking-block--flown');

      const start = (b.start_time || '').slice(0, 5);
      const end = (b.end_time || '').slice(0, 5);
      const pilot = b.pilots?.name || '—';

      block.innerHTML = `
        <div class="calendar-booking-block__day-time">${start}–${end}</div>
        <div class="calendar-booking-block__day-pilot">${pilot}</div>
      `;

      // Tooltip
      const reg = b.aircraft?.registration || '—';
      block.title = `${reg}\n${pilot}\n${start}–${end}\nStatus: ${b.status || '—'}${b.purpose ? '\nPurpose: ' + b.purpose : ''}`;
      block.setAttribute('data-booking-id', b.id || '');

      block.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onBookingClick) onBookingClick(b);
      });

      colOverlay.appendChild(block);
    });

    body.appendChild(colOverlay);
  });

  wrapper.appendChild(body);
  container.appendChild(wrapper);
}
